import React, {createContext, useContext, useReducer, useRef} from 'react';
import {useDispatch} from "react-redux";

import {actions, initialState} from './actions';
import {reducer} from './Referrals.reducer';
import {getMyMembersReferrals, getReferrals} from './api';
import {getGetters} from './getters';
import {getToggles} from './toggles';


const ReferralsContext = createContext();

const ReferralsProvider = ({children}) => {
  const globalDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);
  const referralDetailsCacheRef = useRef({});

  const getters = getGetters(state);
  const toggles = getToggles(dispatch, globalDispatch);
  const updatePagingState = (referralResponse, request, nextList) => {
    const {
      totalSize = 0,
      pageSize = 0,
      pageNumber = 0
    } = referralResponse?.data ?? {};
    toggles.setTotalSize(totalSize);
    toggles.setReferralsSearch({
      ...request,
      pageNumber,
      pageSize
    });
    toggles.setHasMore(nextList.length < totalSize);
  };

  const callbacks = {
    setReferral: payload => dispatch({type: actions.SET_REFERRAL, payload: payload}),
    setQuery: payload => dispatch({type: actions.SET_QUERY, payload: payload}),
    getMyMembersReferrals: async (patientId, filters = [], sorts= [], pageNumber = 1, pageSize = 20) => {
      const request = {filters, sorts, pageNumber, pageSize};
      toggles.setReferralsList([]);
      toggles.setTotalSize(0);
      toggles.setHasMore(true);
      toggles.setIsLoading(true);
      referralDetailsCacheRef.current = {};
      try {
        const referralResponse = await getMyMembersReferrals(patientId, filters, sorts, pageNumber, pageSize);
        const referralDtos = referralResponse?.data?.referralDtos ?? [];
        toggles.setReferralsList(referralDtos);
        updatePagingState(referralResponse, request, referralDtos);
        return referralResponse;
      } catch (error) {
        console.error("Failed to load referrals:", error);
        toggles.setHasMore(false);
        return null;
      } finally {
        toggles.setIsLoading(false);
      }
    },
    scrollMyMembersReferrals: async (patientId) => {
      const request = {
        ...state.referralsSearch,
        pageNumber: (state.referralsSearch?.pageNumber ?? 1) + 1
      };
      toggles.setIsLoading(true);
      try {
        const referralResponse = await getMyMembersReferrals(
          patientId,
          request.filters,
          request.sorts,
          request.pageNumber,
          request.pageSize
        );
        const referralDtos = referralResponse?.data?.referralDtos ?? [];
        const nextList = [
          ...(state.referralsList ?? []),
          ...referralDtos
        ];
        toggles.setReferralsList(nextList);
        updatePagingState(referralResponse, request, nextList);
        return referralResponse;
      } catch (error) {
        console.error("Failed to scroll referrals:", error);
        toggles.setHasMore(false);
        return null;
      } finally {
        toggles.setIsLoading(false);
      }
    },
    getReferralDetails: async (referralId) => {
      if (!referralId) {
        return null;
      }
      if (!referralDetailsCacheRef.current[referralId]) {
        referralDetailsCacheRef.current[referralId] = getReferrals(referralId)
          .then(response => response?.data ?? null)
          .catch(error => {
            delete referralDetailsCacheRef.current[referralId];
            throw error;
          });
      }
      return referralDetailsCacheRef.current[referralId];
    }
  }

  const value = {
    ...getters,
    ...toggles,
    ...callbacks
  }

  return (
    <ReferralsContext.Provider value={value}>
      {children}
    </ReferralsContext.Provider>
  )
};
const useReferralsContext = () => useContext(ReferralsContext);

export {
  ReferralsProvider,
  useReferralsContext,
}
